import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Button, Paper, Typography } from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { Copy as CopyIcon, Info as InfoIcon } from 'react-feather'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import useStyles from './styles'

const normalizeMarkdown = (markdown) =>
  markdown?.replace(/\\n/g, '\n') ?? markdown

const stripMarkdown = (text) => {
  if (!text) return text

  return text
    .replace(/^\|[-: |]+\|$/gm, '')
    .replace(/^\|(.+)\|$/gm, (_, cells) =>
      cells
        .split('|')
        .map((cell) => cell.trim())
        .filter(Boolean)
        .join('\t'),
    )
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*{1,3}([^*\n]+)\*{1,3}/g, '$1')
    .replace(/_{1,3}([^_\n]+)_{1,3}/g, '$1')
    .replace(/^>\s*/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/^[-*_]{3,}\s*$/gm, '')
    .replace(/^[ \t]*[-*+]\s+/gm, '')
    .replace(/^[ \t]*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const renderMarkdown = (markdown) => (
  <Markdown
    remarkPlugins={[remarkGfm]}
    components={{
      a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
    }}
  >
    {markdown}
  </Markdown>
)

const DisputeDocument = ({ documentMd, documentMdAppellative }) => {
  const { DOCUMENT_VERSION_LABEL, DOCUMENT_VERSIONS, SNACKBAR_VARIANTS } =
    constants.dai.disputes

  const classes = useStyles()
  const snackbar = useSnackbar()
  const [version, setVersion] = useState(
    documentMd ? DOCUMENT_VERSIONS.STANDARD : DOCUMENT_VERSIONS.APPELLATIVE,
  )

  const hasStandard = !!documentMd
  const hasAppellative = !!documentMdAppellative
  const hasMultipleVersions = hasStandard && hasAppellative
  const isAppellative = version === DOCUMENT_VERSIONS.APPELLATIVE
  const activeMd = normalizeMarkdown(
    isAppellative ? documentMdAppellative : documentMd,
  )
  const versionLabel = DOCUMENT_VERSION_LABEL[version]

  useEffect(() => {
    if (
      !hasStandard &&
      hasAppellative &&
      version !== DOCUMENT_VERSIONS.APPELLATIVE
    ) {
      setVersion(DOCUMENT_VERSIONS.APPELLATIVE)
    }

    if (
      hasStandard &&
      !hasAppellative &&
      version !== DOCUMENT_VERSIONS.STANDARD
    ) {
      setVersion(DOCUMENT_VERSIONS.STANDARD)
    }
  }, [
    hasAppellative,
    hasStandard,
    version,
    DOCUMENT_VERSIONS.APPELLATIVE,
    DOCUMENT_VERSIONS.STANDARD,
  ])

  const handleVersionChange = (_event, newVersion) => {
    if (newVersion !== null) setVersion(newVersion)
  }

  const handleCopy = () => {
    if (!activeMd) return
    navigator.clipboard.writeText(stripMarkdown(activeMd))
    snackbar.open({
      message: `Contestação ${versionLabel} copiada para a área de transferência.`,
      variant: SNACKBAR_VARIANTS.SUCCESS,
    })
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        {hasMultipleVersions && (
          <Box className={classes.versionGroup}>
            <Typography className={classes.versionLabel}>Versão</Typography>
            <ToggleButtonGroup
              value={version}
              exclusive
              onChange={handleVersionChange}
              size="small"
              className={classes.toggleGroup}
            >
              <ToggleButton
                value={DOCUMENT_VERSIONS.STANDARD}
                classes={{
                  root: classes.toggleButton,
                  selected: classes.toggleButtonSelected,
                }}
              >
                Padrão
              </ToggleButton>
              <ToggleButton
                value={DOCUMENT_VERSIONS.APPELLATIVE}
                classes={{
                  root: classes.toggleButton,
                  selected: classes.toggleButtonSelected,
                }}
              >
                Apelativa
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Button
          variant="outlined"
          color="primary"
          startIcon={<CopyIcon size={14} />}
          onClick={handleCopy}
          disabled={!activeMd}
          size="small"
          className={classes.copyButton}
        >
          {hasAppellative
            ? `Copiar contestação ${versionLabel}`
            : 'Copiar contestação'}
        </Button>
      </Box>

      {isAppellative && (
        <Box className={classes.notice}>
          <InfoIcon size={16} className={classes.noticeIcon} />
          <Typography className={classes.noticeText}>
            Versão apelativa: ignora desclassificações DS.010 formais e
            argumenta pelo mérito material das evidências. Use quando o mérito
            da contestação supere a falha formal.
          </Typography>
        </Box>
      )}

      <Paper variant="outlined" className={classes.documentPaper}>
        <Box className={classes.markdownContainer}>
          {activeMd ? (
            renderMarkdown(activeMd)
          ) : (
            <Typography variant="body2" color="textSecondary">
              Documento ainda não disponível.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

DisputeDocument.propTypes = {
  documentMd: PropTypes.string,
  documentMdAppellative: PropTypes.string,
}

export default DisputeDocument
